/**
 * Utilities for calculating grid-based positioning of elements on a canvas.
 * Used for evenly distributing sections or items in a 2D grid layout.
 */

export interface GridPosition {
	x: number
	y: number
}

export interface SectionSize {
	width: number
	height: number
}

export interface GridLayoutOptions {
	/**
	 * Padding from edges as a percentage (default: 2)
	 */
	padding?: number
	/**
	 * Gap between items as a percentage (default: 2)
	 */
	gap?: number
}

/**
 * Calculate grid position for a new section.
 * Distributes sections evenly in a grid pattern using percentage-based coordinates.
 *
 * @param existingCount - Number of existing sections (determines which position to calculate)
 * @param sectionSize - Width and height of the section as percentages
 * @param options - Optional padding and gap configuration
 * @returns Position { x, y } as percentages
 *
 * @example
 * ```ts
 * const position = calculateGridPosition(0, { width: 20, height: 20 })
 * // Returns: { x: 2, y: 2 } (first item with default 2% padding)
 *
 * const position2 = calculateGridPosition(5, { width: 20, height: 20 })
 * // Returns position for 6th item in the grid
 * ```
 */
export function calculateGridPosition(
	existingCount: number,
	sectionSize: SectionSize,
	options: GridLayoutOptions = {},
): GridPosition {
	const PADDING = options.padding ?? 2 // 2% padding from edges
	const GAP = options.gap ?? 2 // 2% gap between sections

	const usableWidth = 100 - 2 * PADDING

	// Calculate how many sections fit per row
	const sectionsPerRow = Math.floor((usableWidth + GAP) / (sectionSize.width + GAP))
	const columnsPerPage = Math.max(1, sectionsPerRow)

	// Calculate row and column
	const column = existingCount % columnsPerPage
	const row = Math.floor(existingCount / columnsPerPage)

	// Calculate position
	const x = PADDING + column * (sectionSize.width + GAP)
	const y = PADDING + row * (sectionSize.height + GAP)

	// Wrap to visible area if position would be off-canvas
	return {
		x: Math.min(x, 100 - sectionSize.width - PADDING),
		y: Math.min(y, 100 - sectionSize.height - PADDING),
	}
}

// ============================================================================
// Multi-Column Play Grid Layout
// ============================================================================

export interface GridPlay {
	id: string
	playId: string
	name: string
	columnIndex?: number
	rowIndex?: number
}

export type GridCell =
	| { type: 'play'; play: GridPlay; rowIndex: number; columnIndex: number }
	| { type: 'empty'; rowIndex: number; columnIndex: number }
	| { type: 'subsection'; subsectionId: string; rowIndex: number; columnIndex: number }

/**
 * Convert flat play list to 2D grid structure
 * Fills empty cells with { type: 'empty' }
 *
 * Performance: O(n + r*c) where n = plays, r = rows, c = columns
 * Uses pre-indexed Map for O(1) play lookups instead of O(n) find()
 */
export function playsToGrid(plays: GridPlay[], columnCount: number): GridCell[][] {
	if (columnCount === 1 && plays.every(p => p.columnIndex === undefined)) {
		// Simple single-column case - O(n)
		return plays.map((play, index) => [
			{ type: 'play', play, rowIndex: index, columnIndex: 0 }
		])
	}

	// Pre-index plays by position for O(1) lookup - O(n)
	const playIndex = new Map<string, GridPlay>()
	let maxRow = 0
	for (const play of plays) {
		const row = play.rowIndex ?? 0
		const col = play.columnIndex ?? 0
		playIndex.set(`${row},${col}`, play)
		maxRow = Math.max(maxRow, row)
	}

	// Build grid with O(1) lookups - O(r * c)
	const grid: GridCell[][] = []
	for (let row = 0; row <= maxRow; row++) {
		const rowCells: GridCell[] = []
		for (let col = 0; col < columnCount; col++) {
			const play = playIndex.get(`${row},${col}`)
			if (play) {
				rowCells.push({ type: 'play', play, rowIndex: row, columnIndex: col })
			} else {
				rowCells.push({ type: 'empty', rowIndex: row, columnIndex: col })
			}
		}
		grid.push(rowCells)
	}

	return grid
}

/**
 * Convert 2D grid back to flat play list with grid coordinates
 */
export function gridToPlays(grid: GridCell[][]): GridPlay[] {
	const plays: GridPlay[] = []

	grid.forEach((row, rowIndex) => {
		row.forEach((cell, columnIndex) => {
			if (cell.type === 'play') {
				plays.push({
					...cell.play,
					rowIndex,
					columnIndex,
				})
			}
		})
	})

	return plays
}
